import React, { useState } from 'react';
import { Typography, Box, Avatar, Button } from '@mui/material';
import { Circle as CircleIcon, Event as EventIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { formatWhen, RSVP_LABELS } from 'components/collab/events/eventMeta';

/** An event you were invited to (answer Going / Interested here), or one you answered that was cancelled
 *  or moved. */
export default function EventNotificationCard({ notification, onMarkRead }) {
  const navigate = useNavigate();
  const p = notification.payload || {};
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);

  const open = () => {
    if (!notification.is_read && onMarkRead) onMarkRead();
    if (p.event_id) navigate(`/events/${p.event_id}/`);
  };
  const respond = async (e, status) => {
    e.stopPropagation();
    setError(null);
    try {
      await axiosService.post(`/events/${p.event_id}/rsvp/`, { status });
      setAnswer(status);
      if (!notification.is_read && onMarkRead) onMarkRead();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save your answer.'));
    }
  };
  const action = {
    invitation: <> invited you to </>,
    cancelled: <> cancelled </>,
    changed: <> changed the time or place of </>,
  }[p.kind] || <> updated </>;

  return (
    <Box
      onClick={open}
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25, px: 2,
        borderBottom: '1px solid', borderColor: 'divider',
        backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
        cursor: 'pointer', '&:hover': { backgroundColor: 'action.selected' },
      }}
    >
      <Avatar sx={{ width: 34, height: 34, mt: 0.25, bgcolor: p.kind === 'cancelled' ? 'error.main' : '#764ba2', color: 'white' }}>
        <EventIcon sx={{ fontSize: 18 }} />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2">
          <Box component="span" fontWeight={600}>{p.from_user_name || 'Someone'}</Box>
          <Box component="span" color="text.secondary">{action}</Box>
          <Box component="span" fontWeight={600}>{p.event_title || 'an event'}</Box>
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {[formatWhen(p.start_date), p.location].filter(Boolean).join(' · ')}
        </Typography>
        {p.kind === 'invitation' && (
          answer ? (
            <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>
              You answered {RSVP_LABELS[answer]}.
            </Typography>
          ) : (
            <Box display="flex" gap={1} mt={0.75}>
              <Button size="small" variant="contained" onClick={(e) => respond(e, 'going')}>Going</Button>
              <Button size="small" variant="outlined" onClick={(e) => respond(e, 'interested')}>Interested</Button>
              <Button size="small" color="inherit" onClick={(e) => respond(e, 'declined')}>Can't go</Button>
            </Box>
          )
        )}
        {error && <Typography variant="caption" color="error" display="block">{error}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {formatDistanceToNow(new Date(notification.created), { addSuffix: true })}
        </Typography>
        {!notification.is_read && <CircleIcon sx={{ fontSize: 8, color: 'primary.main' }} />}
      </Box>
    </Box>
  );
}
